/**
 * TutorTabBar — le `tabBar` React Navigation de l'espace tuteur : le `<BottomTab>` du DS sur les
 * onglets que le masteur a (Accueil, Mes élèves, Offres de cours avec un droit de mission, Ressources),
 * avec leurs libellés. Calque de `StudentTabBar`.
 *
 * Les pastilles sont celles de l'ancienne barre (`tutorTabScreens.js`, ses `tabBarBadge`), lues par la
 * coquille legacy et descendues ici : les lives du jour, les messages non lus de ses élèves, les
 * offres en attente.
 */
import React from "react"
import { Platform } from "react-native"
import type { BottomTabBarProps } from "@react-navigation/bottom-tabs"
import { useLinkBuilder } from "@react-navigation/native"

import { BottomTab, TABS_BY_TYPE } from "../../../../../design-system"
import { routeNameToTutorTabKey, TUTOR_TAB_KEY_TO_ROUTE, type TutorTabKey } from "./tutorRoutes"

export type TutorTabBarProps = BottomTabBarProps & {
    badges?: Partial<Record<TutorTabKey, number>>
}

export function TutorTabBar(props: TutorTabBarProps) {
    const { state, navigation, badges } = props
    const { buildHref } = useLinkBuilder()
    const present = new Set(state.routeNames)
    const tabs = TABS_BY_TYPE.tutor.filter((key) => present.has(TUTOR_TAB_KEY_TO_ROUTE[key as TutorTabKey]))
    const activeKey = routeNameToTutorTabKey(state.routes[state.index]?.name ?? "") ?? "dashboard"
    // Sur le web, chaque onglet est un lien vers son adresse : Ctrl ou Cmd-clic l'ouvre ailleurs.
    const hrefs =
        Platform.OS === "web"
            ? Object.fromEntries(
                  state.routes.map((route) => [routeNameToTutorTabKey(route.name), buildHref(route.name, route.params)])
              )
            : undefined

    return (
        <BottomTab
            tabs={tabs}
            activeKey={activeKey}
            badges={badges}
            hrefs={hrefs as Partial<Record<TutorTabKey, string>> | undefined}
            onTabChange={(key) => {
                const route = TUTOR_TAB_KEY_TO_ROUTE[key as TutorTabKey]
                // Un nom que le navigateur n'enregistre pas ferait planter `navigate`.
                if (route && present.has(route)) navigation.navigate(route)
            }}
        />
    )
}
